import axios from 'axios';
import type { DateTime } from 'luxon';

export default class GalleryService
{
    public static async getList(filter: FilterModel, pager: Pager)
    {
        return (await axios.get<Pagination<Resource<ListItemModel>>>('admin/cms/gallery', {
            params: { ...filter, ...pager.data() }
        })).data;
    }

    public static async fetch(id: number)
    {
        return (await axios.get<Resource<FormModel>>(`admin/cms/gallery/${id}`)).data;
    }

    public static async create(model: UpsertModel)
    {
        return (await axios.post<Resource<FormModel>>(`admin/cms/gallery`, model)).data;
    }

    public static async update(id: number, model: UpsertModel)
    {
        return (await axios.put<Resource<FormModel>>(`admin/cms/gallery/${id}`, model)).data;
    }

    public static async remove(id: number)
    {
        return (await axios.delete(`admin/cms/gallery/${id}`)).data as Statement;
    }

    public static async status(id: number, status: PublicationEnum)
    {
        return (await axios.patch(`admin/cms/gallery/${id}/status`, {
            status: status
        })).data;
    }

    public static async publish(id: number, model: PublishModel)
    {
        return (await axios.patch(`admin/cms/gallery/${id}/publish`, model)).data;
    }

    public static async attach(id: number, pageId: number)
    {
        return (await axios.post(`admin/cms/gallery/${id}/page/${pageId}`)).data;
    }

    public static async detach(id: number, pageId: number)
    {
        return (await axios.delete(`admin/cms/gallery/${id}/page/${pageId}`)).data;
    }

    public static async getVersions(id: number, pager: Pager)
    {
        return (await axios.get<Pagination<EntityVersion>>(`admin/cms/gallery/${id}/versions`, {
            params: pager.data()
        })).data;
    }

    public static async getPagePublications(id: number)
    {
        return (await axios.get<PagePublication[]>(`admin/cms/gallery/${id}/pages`)).data;
    }

    public static async getAutocompleteList(search: string, limit: number = 10, articleId: number = 0)
    {
        return (await axios.get<ListItemModel[]>('admin/cms/gallery/autocomplete', {
            params: {
                search: search,
                limit: limit,
                articleId: articleId
            }
        })).data;
    }

    /**
     * @param id number
     *
     * @returns Promise<ListItemModel>
     */
    public static async fetchAutocompleteModel(id: number): Promise<ListItemModel>
    {
        return (await axios.get<ListItemModel>(`admin/cms/gallery/autocomplete/${id}`)).data;
    }

    /**
     * @param id number
     *
     * @returns Promise<ListItemModel[]>
     */
    public static async getRelatedFromWidget(id: number): Promise<ListItemModel[]>
    {
        return (await axios.get<ListItemModel[]>(`admin/cms/gallery/${id}/relatedFromWidget`)).data;
    }
}

export interface ListItemModel
{
    id: number;
    title: string;
    createdBy: string;
    datePublishedUtc: DateTime;
    dateDismissedUtc: DateTime;
    status: PublicationEnum;
    ownerId: number;
    resourceId: number;
    author: string;
    originId: number;
}

export interface FilterModel
{
    pageId?: number;
    userId: number;
    status: string;
    title: string;
    dateFromUtc?: DateTime;
    dateDueUtc?: DateTime;
    onlyMine: boolean;
    authorId?: number;
}

export interface UpsertModel
{
    id: number;
    title: string;
    description: string;
    tags: string[];
    photoGallery: File[];
    authorId?: number;
    meta: MetaSettingsModel | null;
}

export interface FormModel extends UpsertModel
{
    datePublishedUtc?: DateTime;
    dateDismissedUtc?: DateTime;
    status: PublicationEnum;
    locale: string;
}

export interface PublishModel
{
    datePublishedUtc?: DateTime;
    dateDismissedUtc?: DateTime;
}
